import { config } from '../../config';
import api from '../../services/api';
import { Webhook } from '../../types/webhooks/Webhook';
import { PatchOperation } from '../../types/requests/PatchOperation';
import { ResponseData } from '../../types/response/ResponseData';
import ResponseError from '../../types/response/ResponseError';
import { deadletterIds, FailedWebhooks } from '../../types/webhooks/FailedWebhook';
import { FailedWebhookBulkRequest } from '../../types/requests/FailedWebhookBulkRequest';

export interface WebhookData {
    url: string;
    subscribedEvents: string[];
    storeIds: string[];
    basicAuth?: string;
    enabled: boolean;
}

export interface WebHookCreatedData {
    entityId: string;
    hmacKey: string;
}

export interface WebHookTestData {
    data: string;
}
export interface WebHookTestResponse {
    data: number;
}

export const listWebhooks = async (
    organizationId: string,
    searchStarted: () => void,
    searchSucceeded: (response: ResponseData<Webhook[]>) => void,
    searchFailed: (error: ResponseError) => void
) => {
    searchStarted();

    if (!organizationId) {
        return;
    }

    try {
        const response = await api.get<ResponseData<Webhook[]>>(
            `organizations/${organizationId}/webhooks`,
            undefined,
            undefined,
            undefined,
            config.WebhookApiHost
        );

        searchSucceeded(response);
    } catch (error) {
        searchFailed(error as ResponseError);
    }
};

export const listFailedWebhooks = async (
    organizationId: string,
    searchFailedWebhooksStarted: () => void,
    searchFailedWebhooksSucceeded: (results: ResponseData<FailedWebhooks>) => void,
    searchFailedWebhooksFailed: (error: ResponseError) => void
) => {
    searchFailedWebhooksStarted();

    if (!organizationId) {
        return;
    }

    try {
        const response = await api.get<ResponseData<FailedWebhooks>>(
            `organizations/${organizationId}/webhooks/deadletters`,
            undefined,
            undefined,
            undefined,
            config.WebhookApiHost
        );

        searchFailedWebhooksSucceeded(response);
    } catch (error) {
        searchFailedWebhooksFailed(error as ResponseError);
    }
};

export const createWebhook = async (
    organizationId: string,
    data: WebhookData,
    creating: () => void,
    creatingSucceeded: (data: WebHookCreatedData) => void,
    creatingFailed: (error: ResponseError) => void,
    searchStarted: () => void,
    searchSucceeded: (response: ResponseData<Webhook[]>) => void,
    searchFailed: (error: ResponseError) => void
) => {
    creating();

    try {
        const response = await api.postWithResponseType<WebhookData, WebHookCreatedData>(
            `organizations/${organizationId}/webhooks`,
            data,
            config.WebhookApiHost
        );

        creatingSucceeded(response);
        await listWebhooks(organizationId, searchStarted, searchSucceeded, searchFailed);
    } catch (error) {
        creatingFailed(error as ResponseError);
    }
};

export const toggleWebhook = async (
    webhook: Webhook,
    patching: () => void,
    patchFailed: (error: ResponseError) => void,
    searchStarted: () => void,
    searchSucceeded: (response: ResponseData<Webhook[]>) => void,
    searchFailed: (error: ResponseError) => void
) => {
    patching();
    try {
        await api.patch<PatchOperation[]>(
            `organizations/${webhook.organization}/webhooks/${webhook.id}`,
            [
                {
                    op: 'replace',
                    path: '/Enabled',
                    value: !webhook.enabled,
                },
            ],
            config.WebhookApiHost
        );

        await listWebhooks(webhook.organization, searchStarted, searchSucceeded, searchFailed);
    } catch (error) {
        patchFailed(error as ResponseError);
    }
};

export const deleteWebhook = async (
    webhook: Webhook,
    deleting: () => void,
    deleteFailed: (error: ResponseError) => void,
    searchStarted: () => void,
    searchSucceeded: (response: ResponseData<Webhook[]>) => void,
    searchFailed: (error: ResponseError) => void
) => {
    deleting();
    try {
        await api.del(
            `organizations/${webhook.organization}/webhooks/${webhook.id}`,
            config.WebhookApiHost
        );

        await listWebhooks(webhook.organization, searchStarted, searchSucceeded, searchFailed);
    } catch (error) {
        deleteFailed(error as ResponseError);
    }
};

export const testWebhook = async (
    webhook: Webhook,
    data: string,
    testing: () => void,
    testingSucceeded: (response: WebHookTestResponse) => void,
    testFailed: (error: ResponseError) => void
) => {
    testing();

    try {
        const response = await api.postWithResponseType<WebHookTestData, WebHookTestResponse>(
            `organizations/${webhook.organization}/webhooks/${webhook.id}/test`,
            { data: data },
            config.WebhookApiHost
        );

        testingSucceeded(response);
    } catch (error) {
        testFailed(error as ResponseError);
    }
};

export const bulkFailedWebhook = async (
    items: deadletterIds[],
    organizationId: string,
    action: string,
    bulkFailedWebhookStarted: () => void,
    bulkFailedWebhookSucceeded: () => void,
    bulkFailedWebhookFailed: (error: ResponseError) => void,
    searchFailedWebhooksStarted: () => void,
    searchFailedWebhooksSucceeded: (results: ResponseData<FailedWebhooks>) => void,
    searchFailedWebhooksFailed: (error: ResponseError) => void
) => {
    bulkFailedWebhookStarted();
    for (let i = 0; i < items.length; i++) {
        const url = `organizations/${organizationId}/webhooks/${items[i].webhookId}/deadletters`;
        const body = {
            deadletterIds: items[i].deadletterIds,
        };

        try {
            action === 'Resend'
                ? await api.postWithResponseType<FailedWebhookBulkRequest, {}>(
                      url,
                      body,
                      config.WebhookApiHost
                  )
                : await api.delWithBody<{}>(url, body, config.WebhookApiHost);
            bulkFailedWebhookSucceeded();

            if (i === items.length - 1) {
                await listFailedWebhooks(
                    organizationId,
                    searchFailedWebhooksStarted,
                    searchFailedWebhooksSucceeded,
                    searchFailedWebhooksFailed
                );
            }
        } catch (error) {
            bulkFailedWebhookFailed(error as ResponseError);
        }
    }
};
